import { Controller } from '@hotwired/stimulus'

const TERMINAL_STATUSES = ['complete', 'failed', 'stopped', 'interrupted']
const TERMINAL_HTTP_STATUSES = [401, 403, 404, 410]
const RETRY_MESSAGE = 'Your photo is still preparing or processing was interrupted. Please check again later.'
// Allow queued/working jobs the full 30-minute window at the capped interval.
const MAX_ATTEMPTS = 200
const DEADLINE_MS = 30 * 60 * 1000
const pollers = new Map()

// Grid and (usually hidden) slideshow placeholders share a request and retry budget.
// Keep the last result while subscribed, including terminal results, so a newly
// connected placeholder can catch up without fetching the same image again.
class ProcessingPoller {
  constructor(url, pendingLimit) {
    this.url = url
    this.pendingLimit = pendingLimit
    this.subscribers = new Set()
    this.abortController = new AbortController()
    this.attempts = 0
    this.pendingAttempts = 0
    this.expiresAt = Date.now() + DEADLINE_MS
  }

  subscribe(callback, pendingLimit) {
    this.pendingLimit = Math.min(this.pendingLimit, pendingLimit)
    this.subscribers.add(callback)
    if (this.result) callback(this.result)
    if (!this.started) {
      this.started = true
      // Also bounds a hung fetch/JSON body, not just completed requests.
      this.deadline = setTimeout(() => this.finish({ message: RETRY_MESSAGE }), DEADLINE_MS)
      this.refresh()
    }
    return () => {
      this.subscribers.delete(callback)
      if (this.subscribers.size === 0) {
        this.stop()
        if (pollers.get(this.url) === this) pollers.delete(this.url)
      }
    }
  }

  stop() {
    this.stopped = true
    clearTimeout(this.timeout)
    clearTimeout(this.deadline)
    this.abortController.abort()
  }

  publish(result) {
    this.result = result
    for (const callback of [...this.subscribers]) {
      if (this.subscribers.has(callback)) callback(result)
    }
  }

  finish(result) {
    this.stop()
    this.publish(result)
  }

  async refresh() {
    if (this.stopped) return
    if (this.attempts >= MAX_ATTEMPTS || Date.now() >= this.expiresAt) {
      this.finish({ message: RETRY_MESSAGE })
      return
    }

    this.attempts += 1
    let status
    let terminalHTTP = false
    try {
      const response = await fetch(this.url, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      })
      if (this.stopped) return
      terminalHTTP = TERMINAL_HTTP_STATUSES.includes(response.status)
      if (response.ok && !terminalHTTP) status = await response.json()
    } catch (_error) {
      // Transient HTTP/network/JSON failures use the same missing-status budget.
    }
    if (this.stopped) return
    if (Date.now() >= this.expiresAt) {
      this.finish({ message: RETRY_MESSAGE })
      return
    }
    if (terminalHTTP) {
      this.finish({ message: 'Image processing status is unavailable. Please refresh the page.' })
      return
    }
    // Readiness, never Sidekiq status alone, authorizes rendering image URLs.
    if (status?.variants_ready) {
      this.finish({ status })
      return
    }
    if (TERMINAL_STATUSES.includes(status?.status)) {
      this.finish({ status, message: 'Image could not be processed. Please delete it and upload the image again.' })
      return
    }
    this.pendingAttempts = status?.status == null ? this.pendingAttempts + 1 : 0
    if (this.pendingAttempts >= this.pendingLimit || this.attempts >= MAX_ATTEMPTS) {
      this.finish({ status, message: RETRY_MESSAGE })
      return
    }
    this.publish({ status })
    if (this.stopped) return
    const delay = Math.min(1000 * (1.5 ** (this.attempts - 1)), 10000, this.expiresAt - Date.now())
    this.timeout = setTimeout(() => this.refresh(), delay)
  }
}

export default class extends Controller {
  static targets = ['bar', 'label']
  // Never request unprocessed variants, even if the job fails or its status expires.
  static values = {
    statusUrl: String,
    variant: { type: String, default: 'grid' },
    pendingAttempts: { type: Number, default: 30 },
  }

  connect() {
    const subscription = {}
    this.subscription = subscription
    let poller = pollers.get(this.statusUrlValue)
    if (!poller) {
      poller = new ProcessingPoller(this.statusUrlValue, this.pendingAttemptsValue)
      pollers.set(this.statusUrlValue, poller)
    }
    subscription.unsubscribe = poller.subscribe(result => {
      // Turbo can remove/replace a frame before Stimulus calls disconnect().
      // A previous connection must not update a reconnected controller either.
      if (this.subscription !== subscription || !this.element.isConnected) return
      this.receive(result)
    }, this.pendingAttemptsValue)
  }

  disconnect() {
    const subscription = this.subscription
    this.subscription = null
    subscription?.unsubscribe()
  }

  receive({ status, message }) {
    // Replacing connected frame contents may already have removed these targets.
    if (status) this.updateProgress(status)
    if (message && this.hasLabelTarget) this.labelTarget.textContent = message
    if (status?.variants_ready) {
      const html = this.variantValue === 'slideshow' ? status.slideshow_media_html : status.media_html
      this.replaceWithMedia(html)
    }
  }

  updateProgress(status) {
    const percentage = Math.min(Math.max(Number.parseInt(status.pct_complete, 10) || 0, 8), 100)
    if (this.hasBarTarget) this.barTarget.style.width = `${percentage}%`
    if (this.hasLabelTarget) this.labelTarget.textContent = status.message || this.messageFor(status.status)
  }

  // Swapping in server-rendered markup makes the placeholder self-healing, rather than
  // depending on the Turbo broadcast landing after the browser has subscribed.
  replaceWithMedia(html) {
    if (!html || !this.element.isConnected) return

    const frame = this.element.closest('turbo-frame')
    if (frame) frame.outerHTML = html
  }

  messageFor(status) {
    if (status === 'complete') return 'Image processed'
    if (TERMINAL_STATUSES.includes(status)) return 'Image processing failed'

    return 'Processing image...'
  }
}
